import { storedDateToLocalDate } from '@/utils/circuits/prices/priceDateKey';
import { addDays, getDay, isBefore, startOfDay } from 'date-fns';
import { WEEKDAYS } from '@/constants/weekdays';
import type {
  HotelSupplement,
  HotelSupplementPricingPeriod,
  HotelSupplementPrice,
  HotelPensionType,
} from '@/types/benefits/hotel/HotelResponse';

/**
 * Returns the daily price of one supplement for the given participants.
 * Applies period/day filters and falls back to adult price when child/adolescent
 * prices are missing. If multiple entries match, the cheapest is used.
 */
export function getHotelSupplementPriceForDate(
  supplement: HotelSupplement,
  date: Date,
  nbAdults: number,
  nbChildren: number,
  nbAdolescents: number,
): number {
  let bestTotal = Infinity;

  const dateDay = startOfDay(date);
  const dayIndex = (getDay(dateDay) + 6) % 7;
  const dayName = WEEKDAYS[dayIndex];

  for (const entry of supplement.pricingList ?? []) {
    const periods = (entry.periods ?? []) as (HotelSupplementPricingPeriod & {
      startDate?: Date | string | null;
      endDate?: Date | string | null;
    })[];

    // The tarif applies to this date if ANY of its periods covers the date
    // and includes the weekday.
    const matchesDate = periods.some((period) => {
      if (!period?.startDate || !period?.endDate) return false;
      const periodStart = storedDateToLocalDate(period.startDate);
      const periodEnd = storedDateToLocalDate(period.endDate);
      if (isBefore(dateDay, periodStart) || isBefore(periodEnd, dateDay))
        return false;
      return (period.applicationDays ?? []).includes(dayName);
    });
    if (!matchesDate) continue;

    const pricing = entry.pricing as HotelSupplementPrice | undefined;
    if (pricing) {
      const adult = pricing.adultPrice ?? 0;
      const effectiveChild = pricing.childPrice ?? pricing.adultPrice ?? 0;
      const effectiveAdolescent =
        pricing.adolescentPrice ?? pricing.adultPrice ?? 0;

      const total =
        adult * nbAdults +
        effectiveChild * nbChildren +
        effectiveAdolescent * nbAdolescents;
      if (total < bestTotal) bestTotal = total;
    }
  }

  return bestTotal === Infinity ? 0 : bestTotal;
}

/**
 * Sums supplements for one date, only for supplements that explicitly include
 * the provided pension type. Supplements with no selected pension type are ignored.
 */
export function getHotelSupplementsTotalForDate(
  supplements: HotelSupplement[],
  date: Date,
  pensionType: HotelPensionType,
  nbAdults: number,
  nbChildren: number,
  nbAdolescents: number,
): number {
  let total = 0;
  for (const supplement of supplements) {
    const applies =
      (supplement.pensionTypes?.length ?? 0) > 0 &&
      (supplement.pensionTypes?.includes(pensionType) ?? false);
    if (applies) {
      total += getHotelSupplementPriceForDate(
        supplement,
        date,
        nbAdults,
        nbChildren,
        nbAdolescents,
      );
    }
  }
  return total;
}

export interface ComputeHotelSupplementPriceForStayParams {
  supplements: HotelSupplement[];
  pensionType: HotelPensionType;
  startDate: Date;
  nbDays: number;
  nbAdults: number;
  nbChildren: number;
  nbAdolescents: number;
  convertPrice: (
    amount: number,
    fromCurrency: string,
    toCurrency: string,
  ) => number;
  fromCurrency: string;
  toCurrency: string;
}

/** Returns the total supplement amount for the stay in target currency. */
export function computeHotelSupplementPriceForStay({
  supplements,
  pensionType,
  startDate,
  nbDays,
  nbAdults,
  nbChildren,
  nbAdolescents,
  convertPrice,
  fromCurrency,
  toCurrency,
}: ComputeHotelSupplementPriceForStayParams): number {
  if (!supplements?.length) return 0;
  let total = 0;
  for (let i = 0; i < nbDays; i++) {
    const day = addDays(startDate, i);
    const dayTotal = getHotelSupplementsTotalForDate(
      supplements,
      day,
      pensionType,
      nbAdults,
      nbChildren,
      nbAdolescents,
    );
    total += convertPrice(dayTotal, fromCurrency, toCurrency);
  }
  return total;
}
